import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SlideOver from '../../shared/components/SlideOver';
import { money, invoiceTotal } from '../../shared/formatters';

const METHODS = ['bank_transfer', 'card', 'cash', 'cheque'];

// Manual payment recording (admin_crud_completion_spec §2.2) — drawer,
// client-prefilled. The amount defaults to the selected invoice's
// outstanding balance; the pb_hook validates (no overpay, no voided
// invoice) and flips the invoice to paid when fully covered.
export default function PaymentForm({ open, onClose, clientId }) {
  const { data, add } = useData();
  const { notify } = useToast();

  const clientInvoices = useMemo(
    () => data.invoices.filter((inv) => inv.client_id === clientId && inv.status !== 'void'),
    [data.invoices, clientId],
  );

  const outstandingOf = (invoiceId) => {
    const invoice = clientInvoices.find((inv) => inv.id === invoiceId);
    if (!invoice) return 0;
    const paid = data.payments
      .filter((p) => p.invoice_id === invoiceId && p.status === 'confirmed' && !p.archived)
      .reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, invoiceTotal(invoice) - paid);
  };

  const [form, setForm] = useState(() => ({
    invoice_id: '',
    amount: '',
    method: 'bank_transfer',
    reference: '',
    date: new Date().toISOString().slice(0, 10),
  }));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const first = clientInvoices.find((inv) => outstandingOf(inv.id) > 0);
      setForm({
        invoice_id: first?.id || '',
        amount: first ? outstandingOf(first.id) : '',
        method: 'bank_transfer',
        reference: '',
        date: new Date().toISOString().slice(0, 10),
      });
    }
  }, [open]);

  const selectInvoice = (invoiceId) => {
    setForm((current) => ({
      ...current,
      invoice_id: invoiceId,
      amount: outstandingOf(invoiceId) || '',
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.invoice_id) {
      notify('Pick an invoice first', 'error');
      return;
    }
    const amount = Number(form.amount);
    if (!(amount > 0)) {
      notify('Enter an amount greater than zero', 'error');
      return;
    }
    setBusy(true);
    try {
      await add('payments', {
        invoice_id: form.invoice_id,
        amount,
        method: form.method,
        reference: form.reference || '',
        date: form.date || null,
        status: 'confirmed',
      });
      notify('Payment recorded');
      onClose();
    } catch (err) {
      notify(err.message || 'Failed to record payment', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} title="Record payment" onClose={onClose}>
      <form className="drawer-form" onSubmit={save}>
        <label>Invoice
          <select value={form.invoice_id} onChange={(event) => selectInvoice(event.target.value)} required>
            <option value="">Choose an invoice…</option>
            {clientInvoices.map((invoice) => {
              const outstanding = outstandingOf(invoice.id);
              return <option key={invoice.id} value={invoice.id} disabled={outstanding <= 0}>{invoice.number} — {money(invoiceTotal(invoice))}{outstanding > 0 ? ` (${money(outstanding)} outstanding)` : ' (paid)'}</option>;
            })}
          </select>
        </label>
        <label>Amount
          <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} required />
        </label>
        <label>Method
          <select value={form.method} onChange={(event) => setForm((current) => ({ ...current, method: event.target.value }))}>
            {METHODS.map((method) => <option key={method} value={method}>{method.replace('_', ' ')}</option>)}
          </select>
        </label>
        <label>Reference<input value={form.reference} onChange={(event) => setForm((current) => ({ ...current, reference: event.target.value }))} placeholder="Optional — e.g. bank reference" /></label>
        <label>Date paid<input type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} required /></label>
        <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : 'Record payment'}</button>
      </form>
    </SlideOver>
  );
}
